import { useCallback, useEffect, useRef, useState, type FocusEvent, type PointerEvent } from 'react';

/**
 * A label that appears where the native `title` never does: on keyboard focus
 * and on a long press, as well as on hover. It is decoration over a control
 * that already carries its own `aria-label`, so it is hidden from assistive
 * technology and never the only place a name lives.
 */
const LONG_PRESS_MS = 450;
const HOLD_MS = 1600;

export function useTooltip(label: string) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clear = useCallback(() => {
    clearTimeout(timer.current);
    clearTimeout(hold.current);
  }, []);
  const hide = useCallback(() => {
    clear();
    setOpen(false);
  }, [clear]);
  useEffect(() => clear, [clear]);

  // Escape closes it, as it closes everything else.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && hide();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, hide]);

  const handlers = {
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType === 'mouse') setOpen(true);
    },
    onPointerLeave: hide,
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      clear();
      // A tap is an action; only a held finger asks "what is this?".
      timer.current = setTimeout(() => {
        setOpen(true);
        hold.current = setTimeout(hide, HOLD_MS);
      }, LONG_PRESS_MS);
    },
    onPointerUp: () => clearTimeout(timer.current),
    onPointerCancel: hide,
    onFocus: (e: FocusEvent<HTMLElement>) => {
      if (e.currentTarget.matches(':focus-visible')) setOpen(true);
    },
    onBlur: hide,
  };

  // An attribute and a pseudo-element (tokens.css), not a child: the label is
  // decoration, so it is never part of the button's own text or its name.
  const tipProps = open ? { 'data-tip': label } : {};

  return { handlers, tipProps };
}
